import React from 'react'
import {
  CustomerInterfaceAuditSnapshotV1,
  CustomerInterfaceAuditMinSnapshotV1,
  IAuditEventData,
} from '../../../api'
import { useDetailStyles } from './useDetailStyles'
import {
  Accordion,
  Box,
  defaultFallbackString,
  Typography,
} from 'cs-kevlar-react'
import { UnavailableDetails } from './UnavailableDetails'
import { resolveEventData, resolveSnapshots, zipSnapshots } from './utils'
import { DetailField } from './DetailField'

interface CustomerInterfaceDetailsProps {
  eventData: IAuditEventData
}

export const CustomerInterfaceDetails: React.FunctionComponent<CustomerInterfaceDetailsProps> = ({
  eventData,
}) => {
  const classes = useDetailStyles()

  const [originalSnapshot, updatedSnapshot] = resolveSnapshots<
    CustomerInterfaceAuditSnapshotV1
  >(eventData, nameof<CustomerInterfaceAuditSnapshotV1>())

  const minSnapshot = resolveEventData<CustomerInterfaceAuditMinSnapshotV1>(
    eventData,
    nameof<CustomerInterfaceAuditMinSnapshotV1>(),
  )

  if (!updatedSnapshot && !originalSnapshot && !minSnapshot)
    return <UnavailableDetails />

  const showDiff = !!originalSnapshot && !!updatedSnapshot
  const configurationOverrides = zipSnapshots(
    originalSnapshot?.configurationOverrides ?? {},
    updatedSnapshot?.configurationOverrides ?? {},
  )

  return (
    <React.Fragment>
      {!!minSnapshot && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Connector"
            original={minSnapshot.customerInterfaceName}
          />
        </Box>
      )}

      {!minSnapshot && (
        <Box>
          <Box className={classes.fieldsContainer}>
            <DetailField
              label="Name"
              showDiff={showDiff}
              original={originalSnapshot?.name}
              updated={updatedSnapshot?.name}
            />

            <DetailField
              label="Notes"
              showDiff={showDiff}
              original={originalSnapshot?.notes}
              updated={updatedSnapshot?.notes}
            />
          </Box>

          <Accordion
            id="CustomerInterfaceDetails:LoggingSettings"
            title="Abstraction Logging Settings"
            defaultExpanded
          >
            <Box className={classes.fieldsContainer}>
              <DetailField
                label="Enable Logging"
                showDiff={showDiff}
                original={originalSnapshot?.loggingConfig?.loggingEnabled?.toString()}
                updated={updatedSnapshot?.loggingConfig?.loggingEnabled?.toString()}
              />

              <DetailField
                label="Log Level"
                showDiff={showDiff}
                original={originalSnapshot?.loggingConfig?.logLevel}
                updated={updatedSnapshot?.loggingConfig?.logLevel}
              />

              <DetailField
                label="Log Target"
                showDiff={showDiff}
                original={
                  originalSnapshot?.loggingConfig?.awsEnabled === true
                    ? 'AWS ELK'
                    : 'Local ELK'
                }
                updated={
                  updatedSnapshot?.loggingConfig?.awsEnabled === true
                    ? 'AWS ELK'
                    : 'Local ELK'
                }
              />
            </Box>
          </Accordion>

          <Accordion
            id="CustomerInterfaceDetails:ConfigurationOverrides"
            title="Configuration Overrides"
            defaultExpanded
          >
            <Box className={classes.fieldsContainer}>
              {configurationOverrides.length === 0 && (
                <Typography className={classes.emptyValue}>
                  {defaultFallbackString}
                </Typography>
              )}

              {configurationOverrides.length > 0 &&
                configurationOverrides.map((x) => (
                  <DetailField
                    key={x.key}
                    label={x.key}
                    showDiff
                    original={x.original}
                    updated={x.updated}
                  />
                ))}
            </Box>
          </Accordion>

          <Accordion
            id="CustomerInterfaceDetails:ImportSettings"
            title="Import Settings"
            defaultExpanded
          >
            <Box className={classes.fieldsContainer}>
              <DetailField
                label="Enable Legacy Document Import Deduping"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableLegacyDocumentImportDeduping?.toString()}
                updated={updatedSnapshot?.importConfig?.enableLegacyDocumentImportDeduping?.toString()}
              />

              <DetailField
                label="Enable Document Import Deduping"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableDocumentImportDeduping?.toString()}
                updated={updatedSnapshot?.importConfig?.enableDocumentImportDeduping?.toString()}
              />

              <DetailField
                label="Enable Document Import Deduping"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableLambdaThumbnailGeneration?.toString()}
                updated={updatedSnapshot?.importConfig?.enableLambdaThumbnailGeneration?.toString()}
              />

              <DetailField
                label="Enable S3 File Document Cleanup"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableS3FileDocumentCleanup?.toString()}
                updated={updatedSnapshot?.importConfig?.enableS3FileDocumentCleanup?.toString()}
              />

              <DetailField
                label="Import Lock Acquisition Delay In Seconds"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.importLockAcquisitionDelayInSeconds?.toString()}
                updated={updatedSnapshot?.importConfig?.importLockAcquisitionDelayInSeconds?.toString()}
              />

              <DetailField
                label="Enable Statusing Document Debug Logs"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableStatusingDocumentDebugLogs?.toString()}
                updated={updatedSnapshot?.importConfig?.enableStatusingDocumentDebugLogs?.toString()}
              />

              <DetailField
                label="Enable Device Avl Push Monitoring"
                showDiff={showDiff}
                original={originalSnapshot?.importConfig?.enableDeviceAvlPushMonitoring?.toString()}
                updated={updatedSnapshot?.importConfig?.enableDeviceAvlPushMonitoring?.toString()}
              />
            </Box>
          </Accordion>
        </Box>
      )}
    </React.Fragment>
  )
}
